import {
  CounterText,
  LoadingSpinner,
  Text,
  Title,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
// TODO: Use our own Button
// eslint-disable-next-line no-restricted-imports
import { Box, Button, DialogContent, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS, InternalLink } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useSearchParams } from "@src/appV2/lib/utils/useSearchParams";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { type PlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/types";
import { useWorkplaceModalsDataContext } from "@src/appV2/redesign/ShiftDiscovery/useWorkplaceModalsDataContext";
import { type WorkplaceProfile } from "@src/appV2/redesign/Workplace/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useRef } from "react";
import { InView } from "react-intersection-observer";
import { useHistory } from "react-router-dom";

import { useCredentialRequirementConfiguration } from "../../Documents/useCredentialRequirementConfiguration";
import { augmentYearsOfExperienceWithWorkerLicenses } from "../../PlacementCandidate/utils";
import {
  transformResponseIntoPlacement,
  useFetchPaginatedPlacements,
} from "../api/useFetchPaginatedPlacements";
import { PlacementCard } from "../components/PlacementCard";
import { useGetPlacementFilters } from "../components/PlacementDetailsPage/hooks/useGetPlacementFilters";
import { PlacementsLoader } from "../components/PlacementsLoader";
import { usePayTransparencyRates } from "../lib/usePayTransparencyRates";
import {
  PLACEMENT_PATHS,
  PLACEMENT_WORKPLACE_PROFILE_PATH,
  type WorkplacePlacementsPathSource,
} from "../paths";
import { usePlacementsFilter } from "../usePlacementsFilter";
import { placementWithDetailsToData } from "../utils/placement-data";

interface WorkplacePlacementsProps {
  workplaceId: string;
  workplace?: WorkplaceProfile;
  modalState: UseModalState;
  placementCandidate: PlacementCandidate;
}

export function WorkplacePlacements(props: WorkplacePlacementsProps) {
  const { workplaceId, modalState, workplace, placementCandidate } = props;

  const history = useHistory();
  const { generateModalPath } = useWorkplaceModalsDataContext();
  const worker = useDefinedWorker();

  const yearsOfExperienceByQualification = augmentYearsOfExperienceWithWorkerLicenses(
    placementCandidate.yearsOfExperienceByQualification,
    worker.licenses
  );

  const [placementsFilter] = usePlacementsFilter();

  const placementFilters = useGetPlacementFilters({
    filter: { workplaceId },
    worker,
    enabled: isDefined(placementCandidate),
  });

  const {
    data: placementsData,
    isLoading: isPlacementsLoading,
    isSuccess: isPlacementsSuccess,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useFetchPaginatedPlacements(
    {
      workerId: worker.userId,
      placementCandidateId: placementCandidate.id ?? "",
      filter: placementFilters,
      shouldLogEvent: true,
    },
    {
      enabled: isDefined(worker.userId),
    }
  );

  const placementsCount = placementsData?.pages.reduce(
    (count, page) => count + page.data.length,
    0
  );

  const { getCachedPayRatesForPlacement, isLoading: isPayDataLoading } = usePayTransparencyRates();

  const scrollRef = useRef<HTMLDivElement>(null);

  const searchParams = useSearchParams();
  const source = searchParams.source as WorkplacePlacementsPathSource;
  const credentialRequirementConfiguration = useCredentialRequirementConfiguration();
  return (
    <>
      <StickyScrollPageHeader
        title={workplace?.name ?? ""}
        isLoading={isPlacementsLoading}
        scrollContainerRef={scrollRef}
      />

      <DialogContent ref={scrollRef} sx={{ px: 7, pt: 0 }}>
        <Stack spacing={6} pt={6}>
          {placementsCount ? (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <CounterText count={placementsCount}>
                <Title
                  bold
                  variant="h5"
                  component="h4"
                  sx={(theme) => ({ fontWeight: theme.typography.fontWeightMedium })}
                >
                  Jobs
                </Title>
              </CounterText>
              {source === "workplace-placements-ad-card" ? (
                <InternalLink
                  to={PLACEMENT_PATHS.ROOT}
                  onClick={() => {
                    logEvent(APP_V2_APP_EVENTS.PLACEMENTS_PAGE_ACTIONS, {
                      placementCandidateId: placementCandidate.id,
                      filters: {
                        ...placementsFilter,
                        distanceInMiles: placementsFilter?.distanceInMiles?.$lt,
                      },
                      action: "explore_all_jobs_clicked",
                    });
                  }}
                >
                  Explore all jobs
                </InternalLink>
              ) : undefined}
            </Stack>
          ) : undefined}

          <Stack spacing={6}>
            {isPlacementsLoading ? <PlacementsLoader /> : undefined}

            {isPlacementsSuccess && (placementsData.pages[0]?.data.length ?? 0) === 0 ? (
              <Stack justifyContent="center" flex={1} alignItems="center">
                <Text variant="body1">No relevant jobs listed yet!</Text>
              </Stack>
            ) : undefined}

            {isDefined(placementsData) && placementsData.pages.length > 0 ? (
              <Stack direction="column" spacing={6}>
                {placementsData?.pages.flatMap((placementPage, pageIndex) => {
                  return placementPage.data.map((placement, placementIndex) => {
                    const placementWithDetails = transformResponseIntoPlacement(
                      placement,
                      placementPage.included,
                      credentialRequirementConfiguration
                    );

                    const { minMaxPayEstimate } = getCachedPayRatesForPlacement(
                      {
                        id: placementWithDetails.id,
                        workplaceId: placementWithDetails.workplace.id,
                        workerTypes: placementWithDetails.workerTypes,
                        jobShifts: placementWithDetails.jobShifts,
                        transparencyPayRates: placementWithDetails.transparencyPayRates,
                        attributes: { payRange: placementWithDetails.payRange },
                      },
                      yearsOfExperienceByQualification,
                      false,
                      placementCandidate.payAndScheduleFormData?.benefits?.combined
                    );

                    return (
                      <PlacementCard
                        key={placementWithDetails.id}
                        placement={placementWithDetailsToData(
                          placementWithDetails,
                          credentialRequirementConfiguration
                        )}
                        placementCandidateId={placementCandidate.id}
                        isRestricted={placementCandidate.isRestricted ?? false}
                        pageIndex={pageIndex}
                        placementIndex={placementIndex}
                        minMaxPayEstimate={minMaxPayEstimate}
                        loadingPayData={isPayDataLoading}
                        showWorkplaceRatings={false}
                        source="workplace_placement_card"
                      />
                    );
                  });
                })}
              </Stack>
            ) : undefined}

            {hasNextPage ? (
              <Stack direction="row" justifyContent="center">
                <Box sx={{ marginY: 4 }}>
                  <InView
                    as="div"
                    onChange={async (inView) => {
                      if (inView && !isFetchingNextPage) {
                        await fetchNextPage();
                        logEvent(APP_V2_APP_EVENTS.PLACEMENTS_PAGE_ACTIONS, {
                          placementCandidateId: placementCandidate.id,
                          filters: {
                            ...placementsFilter,
                            distanceInMiles: placementsFilter?.distanceInMiles?.$lt,
                          },
                          action: "load_more_clicked",
                        });
                      }
                    }}
                  />
                  <Box sx={{ opacity: isFetchingNextPage ? 1 : 0 }}>
                    <LoadingSpinner size="medium" />
                  </Box>
                </Box>
              </Stack>
            ) : undefined}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogFooter
        withShadow
        orientation="horizontal"
        backgroundColor={(theme) => String(theme.palette.background.tertiary)}
        onClose={() => {
          modalState.closeModal();
        }}
      >
        <Button
          fullWidth
          variant="contained"
          size="large"
          sx={{ maxWidth: "60%" }}
          onClick={() => {
            history.push(
              generateModalPath(PLACEMENT_WORKPLACE_PROFILE_PATH, {
                workplaceId,
              })
            );
          }}
        >
          View workplace profile
        </Button>
      </DialogFooter>
    </>
  );
}
